/**
 * Web route registry adapter.
 *
 * Canonical declarative route entries live in packages/contracts. This adapter
 * selects the routes that belong in the current deployable application
 * composition; backend descriptor resolution and authorization remain the
 * security authority.
 */
import {
  APPLICATION_COMPOSITIONS,
  canonicalRouteRegistry,
  filterRoutesForComposition,
  getRouteById as getContractRouteById,
  getRouteByPath as getContractRouteByPath,
  type ApplicationComposition,
} from '@finance/contracts';
import type { RouteDescriptor, RouteId } from '@finance/contracts';

export type WebRouteEntry = RouteDescriptor;

let _webRouteRegistryDeclared = false;

function assertSingleRouteRegistry(): void {
  if (_webRouteRegistryDeclared) {
    throw new Error(
      'A second route registry was declared under apps/web. ' +
      'The canonical route registry lives in packages/contracts/src/experience/routes.',
    );
  }
  _webRouteRegistryDeclared = true;
}

function resolveWebComposition(value: unknown): ApplicationComposition {
  const composition = typeof value === 'string' && value.trim()
    ? value.trim().toUpperCase()
    : 'COMBINED';
  if (!APPLICATION_COMPOSITIONS.includes(composition as ApplicationComposition)) {
    throw new Error(`Unsupported VITE_APPLICATION_COMPOSITION: ${composition}`);
  }
  return composition as ApplicationComposition;
}

assertSingleRouteRegistry();

export const webApplicationComposition = resolveWebComposition(
  import.meta.env.VITE_APPLICATION_COMPOSITION,
);
export const webRouteRegistry: WebRouteEntry[] = filterRoutesForComposition(
  canonicalRouteRegistry,
  webApplicationComposition,
);

export function getRoutesByLayout(layoutId: string): WebRouteEntry[] {
  return webRouteRegistry.filter((route) => route.layoutId === layoutId);
}

export function getRouteByPath(path: string): WebRouteEntry | undefined {
  const exact = webRouteRegistry.find((route) => route.path === path);
  if (exact) return exact;
  for (const route of webRouteRegistry) {
    if (!route.path.includes('/:')) continue;
    const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
    if (new RegExp(`^${pattern}$`).test(path)) return route;
  }
  return undefined;
}

export function getNavigationRoutes(): WebRouteEntry[] {
  return webRouteRegistry.filter(
    (route) => route.navigationPlacement !== 'hidden' && route.availability !== 'HIDDEN',
  );
}

export function getRouteById(routeId: RouteId): WebRouteEntry | undefined {
  const canonical = getContractRouteById(routeId);
  return canonical && webRouteRegistry.some((route) => route.routeId === routeId)
    ? canonical
    : undefined;
}

/** Returns the parent path of a detail route by stripping its trailing param. */
export function getDetailBasePath(routeId: RouteId): string {
  const route = getRouteById(routeId);
  const path = route?.path;
  if (!path) return '';
  return path.replace(/\/:([^/]+)$/, '');
}

// Retain a direct contract lookup only for explicit diagnostics. Product
// navigation must use the composition-scoped selectors above.
export const getCanonicalRouteByPath = getContractRouteByPath;
